import { useEffect, useMemo, useState } from "react";
import { Search, Phone, MapPin, Calendar, ArrowUpRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { formatDateLong } from "@/lib/format";

export default function UsersAdmin() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim();
    if (!s) return users;
    return users.filter((u) => (u.phone || "").includes(s));
  }, [users, q]);

  const openUser = async (u) => {
    setSelected(u);
    setLoadingVisits(true);
    try {
      const { data } = await api.get(`/admin/users/${encodeURIComponent(u.phone)}/visits`);
      setVisits(data);
    } finally { setLoadingVisits(false); }
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return `${d.toLocaleDateString()} · ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } catch { return iso; }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-black">Users</h1>
          <p className="text-gray-500 mt-1">Mobile numbers that verified via OTP. Tap a row to see the events they've explored.</p>
        </div>
        <div className="relative max-w-xs w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            data-testid="users-search-input"
            placeholder="Search by mobile…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-11 h-11 rounded-full"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-black/10 overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="bg-[#F9F8F6] text-xs uppercase tracking-wider text-gray-500 border-b border-black/10">
            <tr>
              <th className="text-left px-6 py-3 font-bold">Mobile</th>
              <th className="text-left px-6 py-3 font-bold">Events explored</th>
              <th className="text-left px-6 py-3 font-bold">Total visits</th>
              <th className="text-left px-6 py-3 font-bold">First seen</th>
              <th className="text-left px-6 py-3 font-bold">Last seen</th>
              <th className="text-right px-6 py-3 font-bold"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {filtered.map((u) => (
              <tr
                key={u.phone}
                data-testid={`user-row-${u.phone}`}
                onClick={() => openUser(u)}
                className="hover:bg-[#F9F8F6] cursor-pointer"
              >
                <td className="px-6 py-4">
                  <div className="inline-flex items-center gap-2 font-bold">
                    <Phone className="h-4 w-4 text-[#FA003F]" />
                    {u.phone}
                  </div>
                </td>
                <td className="px-6 py-4"><span className="font-bold">{u.unique_events_count || 0}</span></td>
                <td className="px-6 py-4">{u.visits_count || 0}</td>
                <td className="px-6 py-4 text-xs text-gray-500">{fmtTime(u.first_seen)}</td>
                <td className="px-6 py-4 text-xs text-gray-500">{fmtTime(u.last_seen)}</td>
                <td className="px-6 py-4 text-right">
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-[#FA003F]">
                    View <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-gray-500">No verified users yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent data-testid="user-visits-dialog" className="max-w-2xl rounded-3xl bg-white p-8 border border-black/10">
          <DialogHeader>
            <div className="h-12 w-12 rounded-2xl bg-[#FA003F]/10 text-[#FA003F] grid place-items-center mb-2">
              <Phone className="h-5 w-5" />
            </div>
            <DialogTitle className="font-display text-2xl font-black tracking-tight">
              {selected?.phone}
            </DialogTitle>
            <DialogDescription>
              {selected?.unique_events_count || 0} unique events · {selected?.visits_count || 0} total visits
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 max-h-[60vh] overflow-y-auto">
            {loadingVisits && <div className="text-center text-gray-500 py-8">Loading…</div>}
            {!loadingVisits && visits.length === 0 && (
              <div className="text-center text-gray-500 py-8">No event visits recorded yet.</div>
            )}
            <ul className="divide-y divide-black/5">
              {visits.map((v) => (
                <li key={v.id} className="py-3 flex items-center gap-4">
                  {v.event_image && (
                    <img src={v.event_image} alt="" className="h-14 w-20 rounded-lg object-cover bg-gray-100" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold line-clamp-1">{v.event_title}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-3 mt-0.5">
                      {v.event_city && (
                        <span className="inline-flex items-center gap-1 capitalize"><MapPin className="h-3 w-3" /> {v.event_city}</span>
                      )}
                      <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtTime(v.visited_at)}</span>
                    </div>
                  </div>
                  <a
                    href={`/event/${v.event_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-bold text-[#FA003F] inline-flex items-center gap-1 hover:underline"
                  >
                    Open <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
